import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { approvalsContext } from 'pages/approvals/approvalsResolver';
import { managePricingContext } from 'pages/managePricing/managePricingResolver';
import { useContext, useState } from 'react';
import { useHttp } from 'shared/providers/provide-http';
import { formatLocalDateToLocalISODateTimeString } from 'shared/utility';
import { ApprovalDashboardData, MarketPriceEditRequest } from 'types/backend';
import { EditModelCurrentPricesForm } from 'types/forms';

export function useConfirmPriceEdit() {
    const [isLoading, setIsLoading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const { refreshData } = useContext(managePricingContext);
    const approvalsContextData = useContext(approvalsContext);
    const [error, setError] = useState<string>();
    const http = useHttp();
    const mapper = (form: EditModelCurrentPricesForm): MarketPriceEditRequest => {
        const request: MarketPriceEditRequest = {
            id: form.id,
            productId: form.productId,
            priceType: form.priceType,
            group: form.group,
            type: form.type,
            gauge: form.gauge,
            length: form.length,
            width: form.width,
            price: Number(form.newPrice),
            targetPrice: form.targetPrice,
            tierOnePrice: form.tierOnePrice,
            start: formatLocalDateToLocalISODateTimeString(form.start),
            end: formatLocalDateToLocalISODateTimeString(form.end),
            status: form.status,
            endUser: form.endUser,
            endUserNumber: form.endUserNumber,
            effectiveStart: form.effectiveStart,
            reason: form.reason,
            reasonNotes: form.reasonNotes,
            competitor: form.competitor,
            competitorProduct: form.competitorProduct,
            competitorPrice: Number(form.competitorPrice),
            estAnnualRevenue: Number(form.estAnnualRevenue),
            priceGroup: form.group,
            approver: form.approver,
            endUserPriceTerms: form.endUserPriceTerms,
            endUserPriceTrigger: form.endUserPriceTrigger,
            isNationalAccount: form.isNationalAccount,
            unitWeight: form.unitWeight,
        };
        return request;
    };

    const onSubmit = async (submission: EditModelCurrentPricesForm) => {
        setIsLoading(true);
        const request = mapper(submission);
        try {
            await http.put(`/api/CurrentPricesUI/editMarketPrice`, request);

            refreshData();

            enqueueSnackbar('Save successful', { variant: 'success' });
        } catch (error) {
            const axiosErrorResponse = (error as AxiosError).response;
            const errorText =
                axiosErrorResponse?.status === 400 && axiosErrorResponse?.data
                    ? (axiosErrorResponse?.data as string)
                    : `${axiosErrorResponse?.statusText} - ${axiosErrorResponse?.status}`;

            setError(errorText);
            enqueueSnackbar('Save failed', { variant: 'error' });
        }

        setIsLoading(false);
    };

    const onStatusSubmit = async (submission: ApprovalDashboardData) => {
        setIsLoading(true);
        try {
            await http.put(`/api/CurrentPricesUI/editStatusCurrentPrice`, submission);

            approvalsContextData.refreshData();

            enqueueSnackbar('Save successful', { variant: 'success' });
        } catch (error) {
            const axiosErrorResponse = (error as AxiosError).response;
            const errorText =
                axiosErrorResponse?.status === 400 && axiosErrorResponse?.data
                    ? (axiosErrorResponse?.data as string)
                    : `${axiosErrorResponse?.statusText} - ${axiosErrorResponse?.status}`;

            setError(errorText);
            enqueueSnackbar('Save failed', { variant: 'error' });
        }

        setIsLoading(false);
    };
    return {
        onSubmit,
        onStatusSubmit,
        isLoading,
        error,
    };
}
